import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Gift, CheckCircle2, Sparkles, Award, ArrowRight, Users, Star, Crown
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalBenefits = () => {
  const [myPlan, setMyPlan] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [planRes, allRes] = await Promise.all([
          axios.get(`${API}/portal/my-plan`, { withCredentials: true }),
          axios.get(`${API}/membership-plans`),
        ]);
        setMyPlan(planRes.data);
        setAllPlans(allRes.data);
      } catch { /* */ } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#E63946] border-t-transparent rounded-full" /></div>;
  }

  const plan = myPlan?.plan;
  const currentPlanIdx = allPlans.findIndex(p => p.id === plan?.id);

  return (
    <div className="px-4 sm:px-6 py-6" data-testid="portal-benefits">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-[#E63946] to-[#F4A261] rounded-xl flex items-center justify-center">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#002249]">My Benefits</h1>
        </div>
        <p className="text-slate-500 text-sm mt-1">Your membership benefits based on your current plan.</p>
      </motion.div>

      {/* Current Plan Card */}
      {plan ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6"
          data-testid="current-plan-card"
        >
          <div className="p-6" style={{ background: `linear-gradient(135deg, ${plan.color || '#002249'}, ${plan.color || '#002249'}dd)` }}>
            <div className="flex items-center justify-between text-white">
              <div>
                <p className="text-white/70 text-xs uppercase tracking-wider font-medium">Your Current Plan</p>
                <h2 className="text-2xl font-bold mt-1 flex items-center">
                  <Crown className="w-6 h-6 mr-2" />{plan.name}
                </h2>
                {plan.name_es && <p className="text-white/60 text-sm italic mt-0.5">{plan.name_es}</p>}
                <p className="text-white/80 text-sm mt-2 max-w-lg">{plan.short_description}</p>
              </div>
              <div className="hidden sm:block text-right">
                <p className="text-3xl font-bold">{plan.price}</p>
                {plan.price_note && <p className="text-white/60 text-xs">{plan.price_note}</p>}
              </div>
            </div>
            {plan.reps_included > 1 && (
              <div className="mt-3 inline-flex items-center bg-white/15 text-white/90 text-xs font-medium px-3 py-1 rounded-full">
                <Users className="w-3.5 h-3.5 mr-1.5" />{plan.reps_included} business representatives included
              </div>
            )}
          </div>

          {/* Benefits by category */}
          {plan.benefit_categories?.length > 0 && (
            <div className="p-6">
              <div className="space-y-5">
                {plan.benefit_categories.map((cat, ci) => (
                  <div key={ci}>
                    <h3 className="text-sm font-bold text-[#002249] uppercase tracking-wide mb-2 flex items-center">
                      <Sparkles className="w-3.5 h-3.5 mr-1.5 text-amber-500" />{cat.category_name}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {cat.items?.map((item, ii) => (
                        <div key={ii} className="flex items-start space-x-2 bg-slate-50 rounded-lg px-3 py-2.5">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {plan.best_for && (
                <div className="mt-5 bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
                  <span className="font-semibold">Best for:</span> {plan.best_for}
                </div>
              )}

              {plan.investment_note && (
                <p className="mt-3 text-sm text-[#E63946] font-medium flex items-center"><Award className="w-4 h-4 mr-1.5" />{plan.investment_note}</p>
              )}

              {plan.validity_text && (
                <p className="mt-2 text-xs text-slate-500">{plan.validity_text}</p>
              )}
            </div>
          )}
        </motion.div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center mb-6">
          <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-bold text-slate-800">No plan assigned yet</h3>
          <p className="text-sm text-slate-500 mt-1">Contact the membership team to get started with a plan.</p>
          <Link to="/portal/support"><Button size="sm" className="mt-3 bg-[#E63946] hover:bg-[#d32f3f] rounded-full">Contact Membership Team</Button></Link>
        </div>
      )}

      {/* Upgrade / Other Plans */}
      {allPlans.length > 1 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h2 className="text-lg font-bold text-[#002249] mb-4">All Available Plans</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            {allPlans.filter(p => p.is_portal_visible).map((p, i) => {
              const isCurrent = p.id === plan?.id;
              return (
                <div key={p.id} className={`bg-white rounded-xl border-2 p-5 transition-all ${isCurrent ? 'border-[#E63946] shadow-md' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`available-plan-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color || '#002249' }} />
                    {isCurrent && <span className="text-[10px] font-bold bg-[#E63946] text-white px-2 py-0.5 rounded-full">Current</span>}
                    {p.is_featured && !isCurrent && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                  </div>
                  <h3 className="font-bold text-[#002249] text-sm">{p.name}</h3>
                  <p className="text-xl font-bold text-slate-900 mt-1">{p.price}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.short_description}</p>
                  {p.reps_included > 1 && <p className="text-xs text-blue-600 mt-1"><Users className="w-3 h-3 inline mr-1" />{p.reps_included} reps</p>}
                  {!isCurrent && (
                    <Link to="/portal/support"><Button size="sm" variant="outline" className="w-full mt-3 rounded-lg text-xs">Inquire about upgrade</Button></Link>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PortalBenefits;
